import { useEffect, useMemo, useRef, useState } from "react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Anchor,
  ArrowRightToLine,
  Check,
  FilePlus2,
  FileText,
  FolderOpen,
  GitBranch,
  Hand,
  Lock,
  LockOpen,
  Move3d,
  PenLine,
  Redo2,
  RefreshCw,
  Save,
  StickyNote,
  Tag,
  Trash2,
  Undo2,
  Wrench,
} from "lucide-react";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import IsoSketchCanvas, { type SketchMode } from "@/components/sketch/IsoSketchCanvas";
import PipeCutList from "@/components/sketch/PipeCutList";
import EstimatePanel from "@/components/sketch/EstimatePanel";
import WeldMapPanel from "@/components/sketch/WeldMapPanel";
import InsulationPanel from "@/components/sketch/InsulationPanel";
import SpoolManager from "@/components/sketch/SpoolManager";
import { exportSpoolSheet } from "@/components/sketch/sketch-pdf";

import { computeSketchTotals, useSketch } from "@/hooks/use-pipe-sketch";
import { useSketchTakeoff } from "@/hooks/use-sketch-takeoff";

import {
  COMPONENT_KINDS,
  componentLabel,
  MATERIALS,
  SUPPORT_KINDS,
  supportLabel,
  TERMINATION_KINDS,
  terminationLabel,
  type ComponentKind,
  type SupportKind,
  type TerminationKind,
} from "@/lib/pipe-spec";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * Help bubble for toolbar buttons.
 * Desktop: hover / keyboard focus. Touch: tap shows the tip for a moment
 * while the button still fires normally.
 */
const Tip = ({ text, children }: { text: string; children: React.ReactElement }) => {
  const [open, setOpen] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );

  const showForTouch = (e: import("react").PointerEvent) => {
    if (e.pointerType === "mouse") return;
    setOpen(true);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpen(false), 2200);
  };

  return (
    <TooltipProvider delayDuration={350}>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild onPointerDown={showForTouch}>
          {children}
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[220px] text-xs">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};




const PipeSketch = () => {
  const s = useSketch();
  const t = useSketchTakeoff();
  const spec = MATERIALS[s.material];
  const [mode, setMode] = useState<SketchMode>("draw");
  const [componentKind, setComponentKind] = useState<ComponentKind>("gate");
  const [supportKind, setSupportKind] = useState<SupportKind>("hanger");
  const [terminationKind, setTerminationKind] = useState<TerminationKind>("cap");
  const [openSaves, setOpenSaves] = useState(false);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [showTags, setShowTags] = useState(true);
  const [showElev, setShowElev] = useState(false);
  const [dimsLocked, setDimsLocked] = useState(false);
  /** which spool the quantity panels report on — "all" = whole sketch */
  const [spoolFilter, setSpoolFilter] = useState<string>("all");
  /** bumped by the Recalculate button to force a fresh roll-up */
  const [recalcKey, setRecalcKey] = useState(0);

  // Runs belonging to the active filter, and every derived entity keyed off them.
  const view = useMemo(() => {
    const all = spoolFilter === "all";
    const runIds = new Set(
      s.runs
        .filter((r) => all || (r.spool ?? "unassigned") === spoolFilter)
        .map((r) => r.id),
    );
    const keep = <T extends { runId: string }>(list: T[]) =>
      all ? list : list.filter((x) => runIds.has(x.runId));
    const cutList = keep(s.cutList);
    const fittings = keep(s.fittings);
    const placements = keep(s.placements);
    const supportPlacements = keep(s.supportPlacements);
    const terminationPlacements = keep(s.terminationPlacements);
    const spanWarnings = keep(s.spanWarnings);
    return {
      cutList,
      fittings,
      placements,
      supportPlacements,
      terminationPlacements,
      spanWarnings,
      totals: computeSketchTotals({
        material: s.material,
        cutList,
        fittings,
        placements,
        supportPlacements,
        terminationPlacements,
      }),
    };
  }, [
    spoolFilter,
    s.runs,
    s.cutList,
    s.fittings,
    s.placements,
    s.supportPlacements,
    s.terminationPlacements,
    s.spanWarnings,
    s.material,
    recalcKey,
  ]);

  // Sync indicator: whenever the scoped roll-up changes, flash "Syncing" then confirm.
  const [syncState, setSyncState] = useState<"idle" | "syncing" | "done">("done");
  const [syncedAt, setSyncedAt] = useState<Date>(() => new Date());
  const sig = JSON.stringify(view.totals) + `|${view.cutList.length}|${spoolFilter}`;
  const lastSig = useRef(sig);
  useEffect(() => {
    if (lastSig.current === sig) return;
    lastSig.current = sig;
    setSyncState("syncing");
    const id = window.setTimeout(() => {
      setSyncState("done");
      setSyncedAt(new Date());
    }, 250);
    return () => window.clearTimeout(id);
  }, [sig]);

  /** Auto-recalculate on scope / spool-assignment changes (persisted preference). */
  const [autoRecalc, setAutoRecalc] = useState<boolean>(() => {
    try {
      return localStorage.getItem("ttb.sketch.autoRecalc") !== "0";
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("ttb.sketch.autoRecalc", autoRecalc ? "1" : "0");
    } catch {
      /* storage unavailable — preference stays in memory */
    }
  }, [autoRecalc]);

  // Signature of scope + every run's spool assignment.
  const assignSig =
    spoolFilter +
    "|" +
    s.runs.map((r) => `${r.id}:${r.spool ?? "unassigned"}`).join(",");
  const lastAssignSig = useRef(assignSig);
  useEffect(() => {
    if (lastAssignSig.current === assignSig) return;
    lastAssignSig.current = assignSig;
    if (!autoRecalc) return;
    setSyncState("syncing");
    setRecalcKey((k) => k + 1);
    const id = window.setTimeout(() => {
      setSyncState("done");
      setSyncedAt(new Date());
    }, 250);
    return () => window.clearTimeout(id);
  }, [assignSig, autoRecalc]);

  const recalculate = () => {
    setSyncState("syncing");
    setRecalcKey((k) => k + 1);
    window.setTimeout(() => {
      setSyncState("done");
      setSyncedAt(new Date());
      toast.success("Totals recalculated");
    }, 250);
  };




  const spoolStats = useMemo(() => {
    const stats: Record<
      string,
      { pieces: number; feet: number; weight: number; welds: number; supports: number }
    > = {};
    const groups = [
      ...s.spools.map((sp) => sp.id),
      "unassigned",
    ];
    for (const id of groups) {
      const runIds = new Set(
        s.runs.filter((r) => (r.spool ?? "unassigned") === id).map((r) => r.id),
      );
      const pick = <T extends { runId: string }>(list: T[]) =>
        list.filter((x) => runIds.has(x.runId));
      const cutList = pick(s.cutList);
      const totals = computeSketchTotals({
        material: s.material,
        cutList,
        fittings: pick(s.fittings),
        placements: pick(s.placements),
        supportPlacements: pick(s.supportPlacements),
        terminationPlacements: pick(s.terminationPlacements),
      });
      stats[id] = {
        pieces: totals.pieces,
        feet: totals.totalFeet,
        weight: totals.totalWeight,
        welds: totals.welds,
        supports: pick(s.supportPlacements).length,
      };
    }
    return stats;
  }, [
    s.spools,
    s.runs,
    s.cutList,
    s.fittings,
    s.placements,
    s.supportPlacements,
    s.terminationPlacements,
    s.material,
  ]);

  const filterLabel =
    spoolFilter === "all"
      ? "Whole sketch"
      : spoolFilter === "unassigned"
        ? "Unassigned runs"
        : (s.spools.find((sp) => sp.id === spoolFilter)?.mark ?? "Spool");
  const scopedName = spoolFilter === "all" ? s.name : `${s.name} — ${filterLabel}`;

  return (
    <div className="pb-20 md:pb-6">
      <PageHeader
        title="Pipe Sketch"
        subtitle="Single-line isometric sketch with auto fittings, valves and cut list"
      />

      <div className="space-y-3 px-3">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={s.name}
            onChange={(e) => s.setName(e.target.value)}
            aria-label="Sketch name"
            className="h-9 w-[170px]"
          />

          <Select value={s.material} onValueChange={(v) => s.setMaterial(v as typeof s.material)}>
            <SelectTrigger className="h-9 w-[210px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.values(MATERIALS).map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={s.size} onValueChange={s.setSize}>
            <SelectTrigger className="h-9 w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {spec.sizes.map((sz) => (
                <SelectItem key={sz.nps} value={sz.nps}>
                  {sz.nps}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-1 rounded-md border border-border p-0.5">
            <Tip text="Draw pipe: tap to set points, each turn drops the right weld fitting">
              <Button
                size="sm"
                variant={mode === "draw" ? "default" : "ghost"}
                className="h-8"
                onClick={() => setMode("draw")}
              >
                <PenLine className="mr-1 h-4 w-4" /> Draw
              </Button>
            </Tip>
            <Tip text="Branch off an existing pipe — a tee is added automatically">
              <Button
                size="sm"
                variant={mode === "branch" ? "default" : "ghost"}
                className="h-8"
                onClick={() => setMode("branch")}
                disabled={!s.runs.length}
              >
                <GitBranch className="mr-1 h-4 w-4" /> Branch
              </Button>
            </Tip>
            <Tip text="Place valves, flanges and other in-line components on a pipe">
              <Button
                size="sm"
                variant={mode === "place" ? "default" : "ghost"}
                className="h-8"
                onClick={() => setMode("place")}
                disabled={!s.runs.length}
              >
                <Wrench className="mr-1 h-4 w-4" /> Place
              </Button>
            </Tip>
            <Tip text="Add hangers, guides and anchors — spacing warnings included">
              <Button
                size="sm"
                variant={mode === "support" ? "default" : "ghost"}
                className="h-8"
                onClick={() => setMode("support")}
                disabled={!s.runs.length}
              >
                <Anchor className="mr-1 h-4 w-4" /> Support
              </Button>
            </Tip>
            <Tip text="Mark an end point: cap, equipment tie-in or continuation">
              <Button
                size="sm"
                variant={mode === "term" ? "default" : "ghost"}
                className="h-8"
                onClick={() => setMode("term")}
                disabled={!s.runs.length}
              >
                <ArrowRightToLine className="mr-1 h-4 w-4" /> End
              </Button>
            </Tip>
            <Tip text="Drop a text note anywhere on the sketch">
              <Button
                size="sm"
                variant={mode === "note" ? "default" : "ghost"}
                className="h-8"
                onClick={() => setMode("note")}
              >
                <StickyNote className="mr-1 h-4 w-4" /> Note
              </Button>
            </Tip>
            <Tip text="Pan and zoom the drawing without editing it">
              <Button
                size="sm"
                variant={mode === "pan" ? "default" : "ghost"}
                className="h-8"
                onClick={() => setMode("pan")}
              >
                <Hand className="mr-1 h-4 w-4" /> Pan
              </Button>
            </Tip>
          </div>

          {mode === "place" && (
            <Select
              value={componentKind}
              onValueChange={(v) => setComponentKind(v as ComponentKind)}
            >
              <SelectTrigger className="h-9 w-[210px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COMPONENT_KINDS.map((k) => (
                  <SelectItem key={k} value={k}>
                    {componentLabel(k)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {mode === "support" && (
            <Select value={supportKind} onValueChange={(v) => setSupportKind(v as SupportKind)}>
              <SelectTrigger className="h-9 w-[210px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SUPPORT_KINDS.map((k) => (
                  <SelectItem key={k} value={k}>
                    {supportLabel(k)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {mode === "term" && (
            <Select
              value={terminationKind}
              onValueChange={(v) => setTerminationKind(v as TerminationKind)}
            >
              <SelectTrigger className="h-9 w-[230px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TERMINATION_KINDS.map((k) => (
                  <SelectItem key={k} value={k}>
                    {terminationLabel(k)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <div className="ml-auto flex items-center gap-2">
            <Tip
              text={
                dimsLocked
                  ? "Dimension lines are locked — tap to allow dragging them"
                  : "Dimension lines can be dragged — tap to lock them in place"
              }
            >
              <Button
                size="sm"
                variant={dimsLocked ? "default" : "outline"}
                className="h-8"
                onClick={() => setDimsLocked((v) => !v)}
                aria-pressed={dimsLocked}
              >
                {dimsLocked ? (
                  <Lock className="mr-1 h-4 w-4" />
                ) : (
                  <LockOpen className="mr-1 h-4 w-4" />
                )}
                Dims
              </Button>
            </Tip>
            <Tip text="Show or hide pipe mark badges (P-01) that match the cut list">
              <Button
                size="sm"
                variant={showTags ? "default" : "outline"}
                className="h-8"
                onClick={() => setShowTags((v) => !v)}
                aria-pressed={showTags}
              >
                <Tag className="mr-1 h-4 w-4" /> Marks
              </Button>
            </Tip>
            <Tip text="Show elevations and N/E coordinates on the sketch">
              <Button
                size="sm"
                variant={showElev ? "default" : "outline"}
                className="h-8"
                onClick={() => setShowElev((v) => !v)}
                aria-pressed={showElev}
              >
                <Move3d className="mr-1 h-4 w-4" /> EL
              </Button>
            </Tip>
            {showElev && (
              <div className="flex items-center gap-1">
                <label htmlFor="base-el" className="text-[11px] text-muted-foreground">
                  Datum EL
                </label>
                <Input
                  id="base-el"
                  type="number"
                  value={s.baseElevation / 12}
                  onChange={(e) => s.setBaseElevation((Number(e.target.value) || 0) * 12)}
                  className="h-8 w-[80px]"
                  aria-label="Datum elevation in feet"
                />
                <span className="text-[11px] text-muted-foreground">ft</span>
              </div>
            )}
            <Tip text="Export an A4 spool sheet PDF of the current scope">
              <Button
                size="sm"
                variant="outline"
                className="h-8"
                disabled={!s.runs.length}
                onClick={() => {
                  exportSpoolSheet({
                    name: scopedName,
                    material: s.material,
                    runs: s.runs,
                    fittings: view.fittings,
                    cutList: view.cutList,
                    supports: view.supportPlacements,
                    terminations: view.terminationPlacements,
                    totals: view.totals,
                    baseElevation: s.baseElevation,
                  });
                  toast.success("Spool sheet exported");
                }}
              >
                <FileText className="mr-1 h-4 w-4" /> PDF
              </Button>
            </Tip>
            {s.activeRunId && (
              <Tip text="End the run you are drawing so you can start another">
                <Button size="sm" onClick={s.finishRun}>
                  <Check className="mr-1 h-4 w-4" /> Finish run
                </Button>
              </Tip>
            )}
            <Tip text="Save this sketch to this device — works offline">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  s.saveSketch();
                  toast.success("Sketch saved to this device");
                }}
                disabled={!s.runs.length}
              >
                <Save className="mr-1 h-4 w-4" /> Save
              </Button>
            </Tip>

            <Dialog open={openSaves} onOpenChange={setOpenSaves}>
              <Tip text="Open a sketch saved on this device">
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline">
                    <FolderOpen className="mr-1 h-4 w-4" /> Open
                  </Button>
                </DialogTrigger>
              </Tip>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Saved sketches</DialogTitle>
                </DialogHeader>
                <div className="space-y-2">
                  {!s.saves.length && (
                    <p className="text-sm text-muted-foreground">
                      Nothing saved yet. Sketches are stored on this device and open offline.
                    </p>
                  )}
                  {s.saves.map((sv) => (
                    <div
                      key={sv.id}
                      className="flex items-center justify-between rounded-md border border-border px-2 py-1.5"
                    >
                      <div>
                        <div className="text-sm font-medium">{sv.name}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {new Date(sv.savedAt).toLocaleString()} · {sv.doc.runs.length} run
                          {sv.doc.runs.length === 1 ? "" : "s"}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            s.loadSketch(sv.id);
                            setOpenSaves(false);
                            setMode("draw");
                          }}
                        >
                          Open
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={`Delete ${sv.name}`}
                          onClick={() => s.deleteSave(sv.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </DialogContent>
            </Dialog>

            <Tip text="Start a brand new blank sketch">
              <Button size="sm" variant="outline" onClick={s.newSketch} aria-label="New sketch">
                <FilePlus2 className="h-4 w-4" />
              </Button>
            </Tip>
            <Tip text="Undo the last action">
              <Button
                size="sm"
                variant="outline"
                onClick={s.undo}
                disabled={!s.canUndo}
                aria-label="Undo"
              >
                <Undo2 className="h-4 w-4" />
              </Button>
            </Tip>
            <Tip text="Redo the action you just undid">
              <Button
                size="sm"
                variant="outline"
                onClick={s.redo}
                disabled={!s.canRedo}
                aria-label="Redo"
              >
                <Redo2 className="h-4 w-4" />
              </Button>
            </Tip>
            <Tip text="Erase everything on the sketch">
              <Button
                size="sm"
                variant="outline"
                onClick={s.clearAll}
                disabled={!s.runs.length}
                aria-label="Clear sketch"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </Tip>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_320px]">
          <div className="trade-card overflow-hidden" style={{ height: "min(70vh, 560px)" }}>
            <IsoSketchCanvas
              runs={s.runs}
              fittings={s.fittings}
              cutList={s.cutList}
              segTrims={s.segTrims}
              placements={s.placements}
              notes={s.notes}
              activeRunId={s.activeRunId}
              material={s.material}
              size={s.size}
              mode={mode}
              componentKind={componentKind}
              onStart={s.startRun}
              onBranch={(hostRunId, p) => {
                s.branchFrom(hostRunId, p);
                setMode("draw");
              }}
              onExtend={s.extendRun}
              onFinish={s.finishRun}
              onUndoVertex={s.removeLastVertex}
              onPlaceComponent={(runId, index, station) =>
                s.addComponent({ runId, index, station, kind: componentKind, size: s.size })
              }
              onAddNote={s.addNote}
              onRemoveComponent={s.removeComponent}
              onRemoveNote={s.removeNote}
              supportPlacements={s.supportPlacements}
              supportKind={supportKind}
              onPlaceSupport={(runId, index, station) =>
                s.addSupport({ runId, index, station, kind: supportKind, size: s.size })
              }
              onRemoveSupport={s.removeSupport}
              terminationPlacements={s.terminationPlacements}
              terminationKind={terminationKind}
              onPlaceTermination={(runId, end) =>
                s.addTermination({ runId, end, kind: terminationKind, size: s.size })
              }
              onRemoveTermination={s.removeTermination}
              baseElevation={s.baseElevation}
              showElev={showElev}
              selectedTag={selectedTag}
              showTags={showTags}
              onSelectTag={setSelectedTag}
              dimOffsets={s.dimOffsets}
              onDimOffset={s.setDimOffset}
              onDimResetOffset={s.resetDimOffset}
              onSetSegmentLength={s.setSegmentLength}
              dimsLocked={dimsLocked}
            />
          </div>
          <div className="trade-card overflow-hidden">
            <Tabs defaultValue="cut" className="flex h-full flex-col">
              <TabsList className="m-2 mb-1 grid w-[calc(100%-1rem)] grid-cols-5">
                <TabsTrigger value="cut" className="px-1 text-xs">Cut</TabsTrigger>
                <TabsTrigger value="estimate" className="px-1 text-xs">Est</TabsTrigger>
                <TabsTrigger value="welds" className="px-1 text-xs">Welds</TabsTrigger>
                <TabsTrigger value="insul" className="px-1 text-xs">Insul</TabsTrigger>
                <TabsTrigger value="spools" className="px-1 text-xs">Spools</TabsTrigger>
              </TabsList>
              <div className="mx-2 mb-2 flex items-center gap-2">
                <span className="shrink-0 text-[11px] text-muted-foreground">Scope</span>
                <Select value={spoolFilter} onValueChange={setSpoolFilter}>
                  <SelectTrigger className="h-7 flex-1 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Whole sketch</SelectItem>
                    {s.spools.map((sp) => (
                      <SelectItem key={sp.id} value={sp.id}>
                        {sp.mark}
                        {sp.name ? ` — ${sp.name}` : ""}
                      </SelectItem>
                    ))}
                    <SelectItem value="unassigned">Unassigned runs</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 shrink-0 px-2 text-xs"
                  onClick={recalculate}
                  aria-label="Recalculate totals"
                >
                  <RefreshCw
                    className={`mr-1 h-3.5 w-3.5 ${syncState === "syncing" ? "animate-spin" : ""}`}
                  />
                  Recalc
                </Button>
              </div>
              <div
                className="mx-2 mb-2 flex items-center gap-1.5 text-[11px]"
                role="status"
                aria-live="polite"
              >
                {syncState === "syncing" ? (
                  <>
                    <RefreshCw className="h-3 w-3 animate-spin text-muted-foreground" />
                    <span className="text-muted-foreground">Syncing totals…</span>
                  </>
                ) : (
                  <>
                    <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-primary/15">
                      <Check className="h-2.5 w-2.5 text-primary" />
                    </span>
                    <span className="text-muted-foreground">
                      Sync complete ·{" "}
                      {syncedAt.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      })}
                    </span>
                  </>
                )}
                <span className="ml-auto flex items-center gap-1.5">
                  <Label htmlFor="auto-recalc" className="text-[11px] text-muted-foreground">
                    Auto
                  </Label>
                  <Switch
                    id="auto-recalc"
                    checked={autoRecalc}
                    onCheckedChange={setAutoRecalc}
                    aria-label="Automatically recalculate totals when scope or spool assignments change"
                    className="scale-75"
                  />
                </span>
              </div>
              <TabsContent
                value="cut"
                className="mt-0 overflow-auto"
                style={{ maxHeight: "min(70vh, 470px)" }}
              >
                <PipeCutList
                  cutList={view.cutList}
                  totals={view.totals}
                  material={s.material}
                  name={scopedName}
                  supports={view.supportPlacements}
                  terminations={view.terminationPlacements}
                  spanWarnings={view.spanWarnings}
                  selectedTag={selectedTag}
                  onSelectTag={setSelectedTag}
                />
              </TabsContent>
              <TabsContent
                value="estimate"
                className="mt-0 overflow-auto"
                style={{ maxHeight: "min(70vh, 470px)" }}
              >
                <EstimatePanel
                  material={s.material}
                  name={scopedName}
                  cutList={view.cutList}
                  fittings={view.fittings}
                  placements={view.placements}
                  supports={view.supportPlacements}
                  terminations={view.terminationPlacements}
                />
              </TabsContent>
              <TabsContent
                value="welds"
                className="mt-0 overflow-auto"
                style={{ maxHeight: "min(70vh, 470px)" }}
              >
                <WeldMapPanel
                  material={s.material}
                  fittings={view.fittings}
                  placements={view.placements}
                  terminations={view.terminationPlacements}
                  weldPositions={t.weldPositions}
                  shopFabDefault={t.shopFabDefault}
                  test={t.test}
                  onSetWeldPosition={t.setWeldPosition}
                  onSetAllWelds={t.setAllWelds}
                  onSetShopFabDefault={t.setShopFabDefault}
                  onSetTest={t.setTest}
                />
              </TabsContent>
              <TabsContent
                value="insul"
                className="mt-0 overflow-auto"
                style={{ maxHeight: "min(70vh, 470px)" }}
              >
                <InsulationPanel
                  material={s.material}
                  cutList={view.cutList}
                  fittings={view.fittings}
                  placements={view.placements}
                  settings={t.takeoff}
                  onChange={t.setTakeoff}
                />
              </TabsContent>
              <TabsContent
                value="spools"
                className="mt-0 overflow-auto"
                style={{ maxHeight: "min(70vh, 470px)" }}
              >
                <SpoolManager
                  spools={s.spools}
                  runs={s.runs}
                  cutList={s.cutList}
                  activeSpool={s.activeSpool}
                  stats={spoolStats}
                  filterSpool={spoolFilter}
                  onFilterSpool={setSpoolFilter}
                  formatFtIn={s.formatFtIn}
                  onSetActiveSpool={s.setActiveSpool}
                  onAddSpool={s.addSpool}
                  onRename={s.renameSpool}
                  onRemove={s.removeSpool}
                  onAssign={s.assignRunToSpool}
                />
              </TabsContent>
            </Tabs>
          </div>

        </div>

        <div className="space-x-3 text-center text-xs text-muted-foreground">
          <span>Click to set the start point, then click along an axis to run pipe</span>
          <span>Turns auto-insert the correct fitting for the material</span>
          <span>Change the size mid-run to drop in a reducer</span>
          <span>Branch mode tees off an existing run</span>
          <span>Place mode drops valves and flange sets in line</span>
          <span>Support mode sets hangers, guides and anchors — tap one again to remove it</span>
          <span>End mode caps a free end or lands it on a pump, vessel or tank nozzle</span>
          <span>EL shows elevations and N/E coordinates against your datum</span>
          <span>PDF exports an A4 spool sheet with the ISO, cut list and schedules</span>
          <span>Note mode adds text and spool tags</span>
          <span>Tap a P-xx mark (or the pipe itself in Pan mode) to jump to its cut-list row</span>
          <span>Save keeps sketches on this device — they open offline</span>
          <span>Lock Dims to stop dimension lines moving while you sketch</span>
          <span>Drag a dimension line to reposition it · double-click it to snap back</span>
          <span>Tap a dimension value to retype it — the spool and cut list update</span>
          <span>Estimate tab prices the spool — labor hours, material, markup and sell price</span>
          <span>Tap any hr/ft or $/ea figure in the estimate to use your own unit — it sticks</span>
          <span>Welds tab numbers every joint W-xx and totals shop vs field diameter inches</span>
          <span>Set design pressure and medium to get the ASME B31.3 test pressure and gauge range</span>
          <span>Insul tab takes off covering, jacket and paint by size with waste</span>
          <span>Spools tab groups runs into shippable pieces — pick one to draw into</span>
          <span>Enter/Esc finish · Backspace undo · Scroll zoom · DIM/BW/+/−/FIT on the sheet</span>

        </div>
      </div>
    </div>
  );
};

export default PipeSketch;
